import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { withPartUpdate } from '@/helpers/rc/miners'
import {
  addPartFailurePictures,
  deleteFailureRepairAction,
  deletePartFailurePictures,
  forceMinerStatusUpdate,
  updateFailure,
  updateMiner,
  updateMinerDiagnostic,
  updateMinerPartWithRepairmentData,
} from '@/services/rc.service'
import { deleteMinerDiagnostic } from '@/services/rc/miners.service'
import type { IMinerDetails } from '@/types/rc/miners'
import type { IMinerDiagnosticForm, IMinerPartRepairmentForm, IMinerUpdateForm } from '@/types/rc/miners/forms'
import type { MinerStatus } from '@/types/rc/miners/MinerStatus'
import { IndividualPart, partTypes } from '@/types/rc/miners/PartType'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { Ref } from 'vue'

export interface UpdateMinerStatusMutation {
  status: MinerStatus
}

export interface UpdateFailureMutation {
  partName: IndividualPart
  failureId: number
  comment?: string
  failure_type_id?: number
  executed_repair_action_id?: number
}

export interface UpdateRepairActionMutation {
  repairChargeId?: number
  failureTypeId?: number
  failureReason?: string
}

export interface DeleteFailureMutation {
  partName: IndividualPart
  failureId: number
}

export type UpdatePartMutation = Omit<IMinerPartRepairmentForm, 'part_type'> & { partName: IndividualPart }

export interface UpdateMinerMutation {
  data: IMinerUpdateForm
}

export interface UpdateDiagnosticMutation {
  diagnostic: IMinerDiagnosticForm
}

export interface UpdatePerformedServicesStateMutation {
  performed_services: number[]
}

export interface UpdateHashboardOnlyMutation {
  hashboard_only: boolean
}

export interface UpdateRepairNotesMutation {
  repair_notes: string
}

export interface AddPicturesMutation {
  failure_id: number
  pictures: File[]
}

export interface DeletePicturesMutation {
  failure_id: number
  pictures: number[]
}

export const useMinerRepairActions = (minerId: Ref<number>) => {
  const client = useQueryClient()

  // PATCH /failures/:id
  const updateFailureMutation = useMutation({
    mutationFn: ({ failureId, failure_type_id, comment, executed_repair_action_id }: UpdateFailureMutation) =>
      updateFailure(failureId, { failure_type_id, comment, executed_repair_action_id }),
    onError: NotifyErrorWrapper('Failed to update failure'),
    onSuccess: (data, args) => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      if (args.executed_repair_action_id) NotifySuccess('Repair charge updated')
      else NotifySuccess('Failure updated')
    },
  })

  // PATCH /failures/:id/repair-action
  const deleteFailureRepairActionMutation = useMutation({
    mutationFn: ({ failureId }: DeleteFailureMutation) => deleteFailureRepairAction(failureId),
    onError: NotifyErrorWrapper('Failed to delete failure'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      NotifySuccess('Repair charge removed')
    },
  })

  // POST /failures/:id/pictures
  const addFailurePicturesMutation = useMutation({
    mutationFn: ({ failure_id, pictures }: AddPicturesMutation) => addPartFailurePictures(failure_id, pictures),
    onError: NotifyErrorWrapper('Failed to upload images'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      NotifySuccess('Pictures uploaded')
    },
  })

  // DELETE /failures/:id/pictures
  const deleteFailurePicturesMutation = useMutation({
    mutationFn: ({ failure_id, pictures }: DeletePicturesMutation) => deletePartFailurePictures(failure_id, pictures),
    onError: NotifyErrorWrapper('Failed to delete images'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      NotifySuccess('Pictures deleted')
    },
  })

  // PUT /miners/:id/parts/repairments
  const updatePartStatusMutation = useMutation({
    mutationFn: (form: UpdatePartMutation) =>
      updateMinerPartWithRepairmentData(minerId.value, { ...form, part_type: partTypes[form.partName] }),
    onError: NotifyErrorWrapper('Failed to update part status'),
    onSuccess: (data, args) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) =>
        withPartUpdate(prev, args.partName, () => data)
      )
      if (args.initial_diagnostic_data) NotifySuccess('Part diagnostic updated')
      else if (args.repair_notes) NotifySuccess('Repair notes updated')
      else NotifySuccess('Part status updated')
    },
  })

  // PATCH /miners/:id/status
  const updateMinerStatusMutation = useMutation({
    mutationFn: ({ status }: UpdateMinerStatusMutation) => forceMinerStatusUpdate(minerId.value, { status }),
    onError: NotifyErrorWrapper('Failed to update miner status'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) => ({ ...prev, status: data.status }))
      NotifySuccess('Miner status updated')
    },
  })

  // PATCH /miners/:id
  const updatePerformedServicesStateMutation = useMutation({
    mutationFn: ({ performed_services }: UpdatePerformedServicesStateMutation) => {
      const data: IMinerUpdateForm = { performed_services: performed_services }
      return updateMiner(minerId.value, data)
    },
    onError: NotifyErrorWrapper('Failed to update performed services'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) => ({
        ...prev,
        performed_services: data.performed_services,
        status: data.status,
      }))
      NotifySuccess(`Performed services updated`)
    },
  })

  // PATCH /miners/:id
  const updateHashboardOnlyMutation = useMutation({
    mutationFn: ({ hashboard_only }: UpdateHashboardOnlyMutation) => {
      const data: IMinerUpdateForm = { hashboard_only: hashboard_only }
      return updateMiner(minerId.value, data)
    },
    onError: NotifyErrorWrapper('Failed to update Hashboard Only flag'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      NotifySuccess(`Hashboard Only flag updated`)
    },
  })

  // PATCH /miners/:id
  const updateRepairNotesMutation = useMutation({
    mutationFn: ({ repair_notes }: UpdateRepairNotesMutation) => updateMiner(minerId.value, { repair_notes }),
    onError: NotifyErrorWrapper('Failed to save notes'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) => ({
        ...prev,
        repair_notes: data.repair_notes,
      }))
      NotifySuccess('Repair notes updated')
    },
  })

  // PATCH /miners/:id/diagnostic
  const updateDiagnosticMutation = useMutation({
    mutationFn: ({ diagnostic }: UpdateDiagnosticMutation) => updateMinerDiagnostic(minerId.value, diagnostic),
    onError: NotifyErrorWrapper('Failed to update diagnostic'),
    onSuccess: (data) => {
      client.setQueryData(['miner-details', minerId], (prev: IMinerDetails) => ({ ...prev, condition: data.condition }))
      NotifySuccess('Diagnostic updated')
    },
  })

  // DELETE /miners/:id/diagnostic
  const deleteMinerDiagnosticMutation = useMutation({
    mutationFn: deleteMinerDiagnostic,
    onError: NotifyErrorWrapper('Failed to delete diagnostic'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['miner-details', minerId] })
      NotifySuccess('Diagnostic deleted')
    },
  })

  return {
    updatePartStatusMutation,
    updateMinerStatusMutation,
    updatePerformedServicesStateMutation,
    updateHashboardOnlyMutation,
    updateRepairNotesMutation,
    updateDiagnosticMutation,
    updateFailureMutation,
    deleteFailureRepairActionMutation,
    deleteMinerDiagnosticMutation,
    addFailurePicturesMutation,
    deleteFailurePicturesMutation,
  }
}
